import { normalizeNetworkLifecycle, type NetworkLifecycle } from '@ukrop-nm/shared';
import { accountStorageKey } from '../auth/accountScope';

export type CustomSiteRecord = {
  id: string;
  title: string;
  createdAt: string;
  lifecycle: NetworkLifecycle;
};

function indexKey() {
  return accountStorageKey('site-index');
}

function diagramNameForSite(siteId: string): string | null {
  try {
    const raw = localStorage.getItem(accountStorageKey(`diagram.${siteId}`));
    if (!raw) return null;
    const doc = JSON.parse(raw) as { name?: string };
    const name = doc.name?.trim();
    return name && name !== siteId ? name : null;
  } catch {
    return null;
  }
}

/** Human-readable title; never prefer technical site id over a stored display name. */
export function getSiteDisplayTitle(site: CustomSiteRecord): string {
  if (site.title?.trim() && site.title !== site.id) return site.title.trim();
  return diagramNameForSite(site.id) ?? site.title?.trim() ?? site.id;
}

function reconcileSiteRecord(site: CustomSiteRecord): CustomSiteRecord {
  const title = getSiteDisplayTitle(site);
  return title === site.title ? site : { ...site, title };
}

export const BUILTIN_SITE_IDS = ['SITE-OFFICE-3F'] as const;
const RETIRED_SITE_IDS = ['SITE-DEMO', 'SITE-PU'] as const;
const CATALOG_FLAG = 'ukrop-nm.catalog-v3-etalon-only';
const BUILTIN = new Set<string>(BUILTIN_SITE_IDS);

export function listCustomSites(): CustomSiteRecord[] {
  try {
    const raw = localStorage.getItem(indexKey());
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CustomSiteRecord[];
    if (!Array.isArray(parsed)) return [];
    const normalized = parsed
      .filter((s) => s?.id && !BUILTIN.has(s.id) && !listDeletedSiteIds().includes(s.id))
      .map((s) => ({
        ...s,
        lifecycle: normalizeNetworkLifecycle(s.lifecycle),
      }));
    const reconciled = normalized.map(reconcileSiteRecord);
    const changed = reconciled.some((s, i) => s.title !== normalized[i]?.title);
    if (changed) {
      localStorage.setItem(indexKey(), JSON.stringify(reconciled));
    }
    return reconciled;
  } catch {
    return [];
  }
}

export function upsertCustomSite(site: CustomSiteRecord) {
  forgetDeletedSite(site.id);
  const next = listCustomSites().filter((s) => s.id !== site.id);
  next.unshift(site);
  localStorage.setItem(indexKey(), JSON.stringify(next));
}

function deletedKey() {
  return accountStorageKey('deleted-sites');
}

export function listDeletedSiteIds(): string[] {
  try {
    const raw = localStorage.getItem(deletedKey());
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === 'string' && Boolean(id));
  } catch {
    return [];
  }
}

export function rememberDeletedSite(siteId: string) {
  if (!siteId || BUILTIN.has(siteId)) return;
  const next = new Set(listDeletedSiteIds());
  next.add(siteId);
  localStorage.setItem(deletedKey(), JSON.stringify([...next]));
}

export function forgetDeletedSite(siteId: string) {
  localStorage.setItem(
    deletedKey(),
    JSON.stringify(listDeletedSiteIds().filter((id) => id !== siteId)),
  );
}

export function replaceDeletedSiteIds(ids: string[]) {
  const unique = [...new Set(ids.filter((id) => id && !BUILTIN.has(id)))];
  localStorage.setItem(deletedKey(), JSON.stringify(unique));
}

export function removeCustomSite(siteId: string) {
  rememberDeletedSite(siteId);
  localStorage.setItem(
    indexKey(),
    JSON.stringify(listCustomSites().filter((s) => s.id !== siteId)),
  );
}

export function isBuiltinSite(siteId: string): boolean {
  return BUILTIN.has(siteId);
}

export function allocateSiteId(title: string): string {
  const ascii = title
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 20);
  const fallback = `PROJ-${Date.now().toString(36).toUpperCase()}`;
  const base = ascii ? `SITE-${ascii}` : `SITE-${fallback}`;
  const taken = new Set([...BUILTIN, ...listCustomSites().map((s) => s.id)]);
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

/** One-time: drop DEMO/ПУ and leftover local projects so the catalog is etalon-only. */
export function purgeRetiredCatalog() {
  try {
    if (localStorage.getItem(CATALOG_FLAG) === '1') return;
    const customIds = listCustomSites().map((s) => s.id);
    const ids = new Set<string>([...RETIRED_SITE_IDS, 'SITE-OFFICE-3F', ...customIds]);
    for (const id of ids) {
      localStorage.removeItem(`ukrop-nm.diagram.${id}`);
      localStorage.removeItem(`ukrop-nm.diagram.versions.${id}`);
    }
    localStorage.setItem('ukrop-nm.site-index', '[]');
    localStorage.setItem(CATALOG_FLAG, '1');
  } catch {
    /* private mode / tests without storage */
  }
}

purgeRetiredCatalog();
